/**
 * «Правила и антиспам»: разбор ввода и сборка патча организации (ТЗ §8, §10.2, §14.2).
 *
 * Логика вынесена из экрана в отдельный модуль намеренно: прогон тестов идёт в окружении
 * `node` без jsdom (см. `vite.config.ts`), поэтому проверить можно только то, что не
 * требует DOM. Здесь живёт ровно то, что молча ломается: разбор чисел и состав патча.
 *
 * ЗНАЧЕНИЯ БЕРУТСЯ ИЗ `CabinetBusinessDto`, А НЕ ИЗ ПРЕСЕТА ОТРАСЛИ. Пресет копируется
 * в строку организации при создании и после этого не читается (AGENTS.md, правило 5):
 * владелец мог поднять окно отмены с 2 до 24 часов, и показать ему пресетные 2 значило бы
 * соврать про его же настройку — причём соврать правдоподобно.
 */

import { DEFAULT_REMINDER_LEAD_HOURS } from '@app/shared';
import type { CabinetBusinessDto, UpdateBusinessInput } from '../../../api/cabinetTypes';
import type { UiKey } from '../../../i18n/dict';

export type RuleFieldKey =
  | 'slotStepMin'
  | 'cancelWindowHours'
  | 'maxActiveBookings'
  | 'cooldownMinutes'
  | 'localBanDays';

export interface RuleField {
  readonly key: RuleFieldKey;
  readonly labelKey: UiKey;
  /** Чем грозит значение. Владелец правит защиту от спама — он обязан понимать цену правки. */
  readonly noteKey: UiKey;
  readonly min: number;
  readonly max: number;
}

/**
 * Границы ПОВТОРЯЮТ `LIMITS` из `packages/backend/src/services/businessService.ts`.
 *
 * Это осознанный дубль, а не забытая общая константа: в `@app/shared` их сегодня нет, а
 * править чужой пакет в рамках этой задачи нельзя. Разъехавшись, копии дадут не тихую
 * поломку, а лишний отказ `err.validation` с полем, минимумом и максимумом от сервера —
 * то есть худшее, что здесь случится, владелец увидит текстом. Авторитет всё равно
 * у бэкенда: он проверяет тот же диапазон заново.
 *
 * Ноль у `maxActiveBookings` и `localBanDays` запрещён не для красоты. Ноль активных
 * записей отказывал бы КАЖДОМУ клиенту ключом `err.tooManyActive`, а нулевой срок бана
 * роняет расчёт санкций — оба дефекта владелец нашёл бы только по жалобам.
 */
export const RULE_FIELDS: readonly RuleField[] = [
  {
    key: 'slotStepMin',
    labelKey: 'ruleSlotStep',
    noteKey: 'ruleSlotStepNote',
    min: 5,
    max: 240,
  },
  {
    key: 'cancelWindowHours',
    labelKey: 'ruleCancelWindow',
    noteKey: 'ruleCancelWindowNote',
    min: 0,
    max: 24 * 7,
  },
  {
    key: 'maxActiveBookings',
    labelKey: 'ruleMaxActive',
    noteKey: 'ruleMaxActiveNote',
    min: 1,
    max: 50,
  },
  {
    key: 'cooldownMinutes',
    labelKey: 'ruleCooldown',
    noteKey: 'ruleCooldownNote',
    min: 0,
    max: 24 * 60,
  },
  {
    key: 'localBanDays',
    labelKey: 'ruleLocalBan',
    noteKey: 'ruleLocalBanNote',
    min: 1,
    max: 365,
  },
];

export const RULE_FIELD_BY_KEY: Readonly<Record<RuleFieldKey, RuleField>> = Object.fromEntries(
  RULE_FIELDS.map((field) => [field.key, field]),
) as Readonly<Record<RuleFieldKey, RuleField>>;

/** Почему ввод отвергнут. Ключи i18n подбираются по этому значению, а не текстом. */
export type RuleIssue = 'empty' | 'notNumber' | 'range';

export type RuleParse =
  | { readonly ok: true; readonly value: number }
  | { readonly ok: false; readonly issue: RuleIssue };

/**
 * Разбор числового поля.
 *
 * ПУСТАЯ СТРОКА — ОШИБКА, А НЕ НОЛЬ. `Number('')` возвращает 0, и наивный `Number(raw) || 0`
 * превратил бы очищенное поле «максимум активных записей» в ноль — то есть в отказ каждому
 * клиенту, — а очищенный cooldown в «без паузы», сняв защиту от спама молча
 * (AGENTS.md, правило 7). Поэтому разбор идёт регуляркой по строке, а не приведением типа.
 *
 * Дробное и отрицательное тоже отсекаются: колонки в базе целые, а «-1 час окна отмены»
 * не имеет смысла ни в одной отрасли.
 */
export function parseRuleNumber(raw: string, field: RuleField): RuleParse {
  const text = raw.trim();
  if (text.length === 0) return { ok: false, issue: 'empty' };
  if (!/^\d+$/.test(text)) return { ok: false, issue: 'notNumber' };

  const value = Number(text);
  // 20-значное число проходит регулярку, но теряет точность в double: до сервера уехало бы
  // не то, что набрал владелец.
  if (!Number.isSafeInteger(value)) return { ok: false, issue: 'notNumber' };
  if (value < field.min || value > field.max) return { ok: false, issue: 'range' };
  return { ok: true, value };
}

/**
 * Черновик формы.
 *
 * Числа держатся СТРОКАМИ: поле в процессе набора бывает пустым, а хранить его числом
 * значило бы выбрать, чем пустоту заменить, — и любой выбор здесь был бы тихим фолбэком.
 *
 * Числовые поля собраны в отдельный `numbers`, а не разложены по корню: правка идёт
 * по ключу поля (`numbers[key] = value`), и в плоской структуре такая запись задевала бы
 * и `reminderLeadHours`, у которого тип совсем другой.
 */
export interface RulesDraft {
  readonly numbers: Readonly<Record<RuleFieldKey, string>>;
  readonly reminderLeadHours: readonly number[];
}

export function rulesDraftFrom(business: CabinetBusinessDto): RulesDraft {
  return {
    numbers: {
      slotStepMin: String(business.slotStepMin),
      cancelWindowHours: String(business.cancelWindowHours),
      maxActiveBookings: String(business.maxActiveBookings),
      cooldownMinutes: String(business.cooldownMinutes),
      localBanDays: String(business.localBanDays),
    },
    reminderLeadHours: [...business.reminderLeadHours],
  };
}

export type RuleErrors = Readonly<Partial<Record<RuleFieldKey | 'reminderLeadHours', RuleIssue>>>;

export type RulesPatchResult =
  | { readonly ok: true; readonly patch: UpdateBusinessInput; readonly changed: boolean }
  | { readonly ok: false; readonly errors: RuleErrors };

/**
 * Варианты интервалов напоминаний для переключателей.
 *
 * Бэкенд принимает только те часы, под которые в схеме есть значение `ReminderKind` —
 * сегодня это 24 и 2 (ТЗ §11). Отдельной константы «поддерживаемые» в `@app/shared` нет,
 * поэтому за основу берётся `DEFAULT_REMINDER_LEAD_HOURS`, и к ней добавляется то, что
 * уже стоит у организации. Второе слагаемое важнее первого: расширь бэкенд набор, и
 * организация с интервалом «за 6 часов» иначе увидела бы форму без него, а сохранение
 * тихо стёрло бы настройку, которой владелец не видел.
 */
export function reminderOptions(current: readonly number[]): readonly number[] {
  return [...new Set([...DEFAULT_REMINDER_LEAD_HOURS, ...current])].sort((a, b) => b - a);
}

function sameHours(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * Патч правил: только изменённые поля и НИ ОДНОГО лишнего.
 *
 * `slug` и `type` сюда не попадают ни при каком вводе — их нет ни в черновике, ни в
 * `UpdateBusinessInput`. Бэкенд их отвергает, а не игнорирует, и это правильно: молча
 * проглоченный `slug` дал бы владельцу уверенность, что публичный адрес сменился, когда
 * на QR-наклейках у клиентов остался старый.
 *
 * Неизменённые поля не отправляются: патч из всех шести значений на каждое сохранение
 * затирал бы чужую параллельную правку той же организации значениями, набранными до неё.
 */
export function buildRulesPatch(
  current: CabinetBusinessDto,
  draft: RulesDraft,
): RulesPatchResult {
  const errors: Record<string, RuleIssue> = {};
  const parsed: Partial<Record<RuleFieldKey, number>> = {};

  for (const field of RULE_FIELDS) {
    const result = parseRuleNumber(draft.numbers[field.key], field);
    if (result.ok) parsed[field.key] = result.value;
    else errors[field.key] = result.issue;
  }

  // Пустой набор напоминаний запрещён и бэкендом: напоминания — главная ценность продукта
  // (сокращение неявок), и выключить их целиком случайным снятием обоих флажков нельзя.
  if (draft.reminderLeadHours.length === 0) errors['reminderLeadHours'] = 'empty';

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const patch: UpdateBusinessInput = {};
  for (const field of RULE_FIELDS) {
    const value = parsed[field.key];
    if (value !== undefined && value !== current[field.key]) patch[field.key] = value;
  }

  const hours = [...draft.reminderLeadHours].sort((a, b) => b - a);
  if (!sameHours(hours, [...current.reminderLeadHours].sort((a, b) => b - a))) {
    patch.reminderLeadHours = hours;
  }

  return { ok: true, patch, changed: Object.keys(patch).length > 0 };
}
